import { IPresetParams } from "@/api/types/stats";
import router from "@/router";
import store from "@/store";
import { IFilter } from "@/types";
import {
    CompareOffsetType,
    getCompareGapInDays,
    getCompareOffset,
    getComparePeriodByOffset,
    ICompareOffset,
} from '@/utils/comparePeriod';
import {
    getCurrentDate,
    getCurrentWeek,
    getCurrentYear,
    getFullCurrentMonth,
    getFullCurrentWeek,
    getFullPreviousMonth,
    getFullPreviousWeek,
    getPreviousDate,
} from '@/utils/formatDates';
import isEqual from "lodash-es/isEqual";
import { useStatsCompareStore } from "../../store_v3/StatsCompareStore";

const groupByTemplate = [
    ['dm_lead_date_hour', 'hour'], ['dm_lead_date', 'date'],
    ['dm_lead_date_week', 'week'], ['dm_lead_date_month', 'month'],
    ['dm_lead_date_year', 'year'],
];

enum DATE_TEMPLATE {
    'Сегодня' = 'today',
    'Вчера' = 'yesterday',
    'Последние 7 дней' = 'days7',
    'За текущую неделю' = 'currentWeek',
    'За прошлую неделю' = 'lastWeek',
    'За текущий месяц' = 'currentMonth',
    'За прошлый месяц' = 'lastMonth',
    'За текущий год' = 'year',
    'Произвольный период' = 'custom',
}

const dateFormat = 'yyyy-MM-dd';
const getDateTemplateValue = () => ({
    today: getCurrentDate(dateFormat),
    yesterday: getPreviousDate(dateFormat),
    days7: getCurrentWeek(dateFormat),
    currentWeek: getFullCurrentWeek(dateFormat),
    lastWeek: getFullPreviousWeek(dateFormat),
    currentMonth: getFullCurrentMonth(dateFormat),
    lastMonth: getFullPreviousMonth(
        dateFormat,
        new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
    ),
    year: getCurrentYear(dateFormat, new Date(new Date().getFullYear(), 0, 1)),
});

// Единица сдвига сравнения по ТЗ: месячные пресеты в месяцах, годовой в годах, остальные в днях.
const COMPARE_OFFSET_TYPE: Record<string, CompareOffsetType> = {
    currentMonth: 'offset_months',
    lastMonth: 'offset_months',
    year: 'offset_years',
};

// Пресеты, у которых основной период растёт каждый день.
const GROWING_PERIODS = ['currentWeek', 'currentMonth', 'year'];

const findTmp = (name: string = 'without_lead_date'): string[] | undefined => {
    return name !== 'without_lead_date' ? groupByTemplate.find(i => i.includes(name)) : [name];
};

export const preparePresetData = (title: string, filters: IFilter[]): IPresetParams => {
    const statsCompareStore = useStatsCompareStore();
    const query: any = router.currentRoute.query;
    const date = filters.find(el => el.id === 'date');
    const data: IPresetParams = {
        name: '',
        query: {
            columns: [],
            filters: [],
            period: {
                type: '',
                value: [],
            },
            form_by: '',
            currency: '',
            sorting: {
                column: '',
                direction: '',
            },
        },
    };

    data.name = title;
    data.query.columns = query.columns;
    data.query.currency = query.convert_currency || '';
    data.query.form_by = query.date_click?.replace('-', '_');

    if (query.group !== 'without_lead_date') {
        data.query.group_by = findTmp(query.group)?.[1];
    }

    data.query.sorting.column = query.sort_by;
    data.query.sorting.direction = query.sort_desc ? 'desc' : 'asc';
    data.query.period = { type: 'today', value: [] };

    if (statsCompareStore.isComparePeriod) {
        const mainType = date?.periodTemplate !== undefined ? DATE_TEMPLATE[date.periodTemplate] : 'custom';
        const [compareDateFrom, compareDateTo] = statsCompareStore.compareDate;
        const mainDateFrom = query.date_from as string;

        if (!compareDateFrom || !compareDateTo || mainType === 'custom') {
            data.query.prev_period = { type: 'custom', value: statsCompareStore.compareDate };
        } else {
            // У растущего пресета сравнение вплотную пишется сдвигом в 1 день: при
            // открытии он схлопывается до примыкания при любой длине основного периода.
            // Сдвиг на длину периода на день сохранения через неделю или месяц, когда
            // период начнётся заново и станет короче, оторвал бы сравнение от основного.
            const offset: ICompareOffset =
                GROWING_PERIODS.includes(mainType) && getCompareGapInDays(mainDateFrom, compareDateTo) === 1
                    ? { type: 'offset_days', value: 1 }
                    : getCompareOffset(mainDateFrom, compareDateFrom, COMPARE_OFFSET_TYPE[mainType] || 'offset_days');

            data.query.prev_period = { type: offset.type, value: [String(offset.value)] };
        }
    }

    if (date?.select !== undefined && date?.periodTemplate !== undefined) {
        data.query.period.type = DATE_TEMPLATE[date?.periodTemplate];
        data.query.period.value = DATE_TEMPLATE[date?.periodTemplate] === 'custom' ? date.select : [];
    }

    for (const i in query) {
        if (['columns', 'date_from', 'date_to', 'sort_by', 'sort_desc', 'offset', 'group', 'date_click', 'convert_currency', 'compare_date_from', 'compare_date_to'].includes(i)) continue;

        if (query[i].length !== 0) {
            data.query.filters.push({
                column: i + '.' + typeof query[i],
                values: Array.isArray(query[i]) ? query[i] : [query[i]],
            });
        }
    }

    return data;
};

export const prepareQueryPresetData = (presets: IPresetParams[]): IPresetParams[] => {
    return presets.map((el: IPresetParams) => {
        const query: any = {};

        query.columns = el.query.columns;
        query.date_click = el.query.form_by.replace('_', '-');
        query.group = findTmp(el.query.group_by)?.[0];
        query.sort_by = el.query.sorting.column;
        query.sort_desc = el.query.sorting.direction === 'desc';

        if (el.query.period.type === 'custom') {
            query.date_from = el.query.period.value[0];
            query.date_to = el.query.period.value[1];
        } else {
            const date = getDateTemplateValue()[el.query.period.type];
            query.date_from = Array.isArray(date) ? date[0] : date;
            query.date_to = Array.isArray(date) ? date[1] : date;
        }

        if (!!el.query.prev_period) {
            const prevPeriod = el.query.prev_period;

            if (prevPeriod.type === 'custom') {
                query.compare_date_from = prevPeriod.value[0];
                query.compare_date_to = prevPeriod.value[1];
            } else {
                const type: CompareOffsetType = ['offset_months', 'offset_years'].includes(prevPeriod.type)
                    ? prevPeriod.type as CompareOffsetType
                    : 'offset_days';
                const value = parseInt(prevPeriod.value[0], 10) || 1;

                [query.compare_date_from, query.compare_date_to] =
                    getComparePeriodByOffset(query.date_from, query.date_to, { type, value });
            }
        }

        if (el.query.currency) {
            query.convert_currency = el.query.currency;
        }

        el.query.filters?.forEach(e => {
            const splitVal = e.column.split('.');

            query[splitVal[0]] = splitVal[1] === 'string' ||
                splitVal[1] === 'boolean' ||
                    splitVal[1] === 'number' ? String(e.values[0]) : e.values.map(m => String(m));
        });

        return {
            id: el.id as string,
            name: el.name,
            query,
        };
    });
};

export const findSimilarPreset = (data: IPresetParams): IPresetParams[] => {
    const presets: IPresetParams[] = JSON.parse(JSON.stringify(store.getters['GET_REPORT_PRESETS']));
    return presets.filter(el => isEqual(data.query, el.query));
};

export const updateRouterFavoriteId = (id: string = 'nf'): void => {
    router.replace({
        params: {
            favorite_id: String(id),
        },
        query: {
            ...router.currentRoute.query,
        },
    });
};

export const setWindowTitle = (text: string): void => {
    window.document.title = text;
};
